import { lazy, Suspense, ReactNode } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { SSOProvider } from './SSOProvider';
import SSOCallback from '../components/pages/SSOCallback';
import { PAGE_ROUTES } from '../constants/common';
import PageLoader from '../components/molecules/PageLoader';
import RootLayout from '../components/layouts/RootLayout';
import AdminProtectedRoute from './AdminProtectedRoute';

// Lazy load page components
const HomeScreen = lazy(() => import('../components/pages/HomeScreen'));
const MarketingDashboard = lazy(
  () => import('../components/pages/MarketingDashboard')
);
const UserManagement = lazy(() => import('../components/pages/UserManagement'));
const FileUploadDashboard = lazy(
  () => import('../components/pages/FileUploadDashboard')
);
const AgenticPlannerLoader = lazy(
  () => import('../components/pages/AgenticPlannerLoader')
);
const AccessForbidden = lazy(
  () => import('../components/pages/AccessForbidden')
);
const KnowledgeCenterPage = lazy(
  () => import('../components/widgets/KnowledgeCenter')
);
const ImageGenerationPage = lazy(
  () => import('../components/pages/ImageGenerationPage')
);

// Suspense wrapper helper
const withSuspense = (element: ReactNode) => (
  <Suspense fallback={<PageLoader />}>{element}</Suspense>
);

export const router = createBrowserRouter([
  // SSO Callback - outside of main layout
  {
    path: PAGE_ROUTES.CALLBACK,
    element: <SSOCallback />,
  },
  // Main app routes with shared layout
  {
    path: PAGE_ROUTES.DEFAULT,
    element: (
      <SSOProvider>
        <RootLayout />
      </SSOProvider>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={PAGE_ROUTES.HOME} replace />,
      },
      {
        path: 'home',
        element: withSuspense(<HomeScreen />),
      },
      {
        path: 'automation',
        element: withSuspense(<div>GenAI Automation</div>),
      },
      {
        path: 'generate-content',
        element: withSuspense(<MarketingDashboard />),
        children: [
          {
            path: 'knowledge-center',
            element: <KnowledgeCenterPage />,
          },
        ],
      },
      {
        path: 'user-management',
        element: (
          <AdminProtectedRoute fallback={withSuspense(<AccessForbidden />)}>
            {withSuspense(<UserManagement />)}
          </AdminProtectedRoute>
        ),
      },
      {
        path: 'upload-and-generate',
        element: withSuspense(<FileUploadDashboard />),
      },
      {
        path: 'agentic-planner/*',
        element: withSuspense(<AgenticPlannerLoader />),
      },
      {
        path: 'image-generation',
        element: withSuspense(<ImageGenerationPage />),
      },
      // Catch-all redirect
      {
        path: '*',
        element: <Navigate to={PAGE_ROUTES.HOME} replace />,
      },
    ],
  },
]);
